import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Box, Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, useTheme, Tooltip, Card, TextField, InputAdornment, IconButton, TableSortLabel, Collapse, Button, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentsIcon from '@mui/icons-material/Payments';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CategoryIcon from '@mui/icons-material/Category';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LinkIcon from '@mui/icons-material/Link';
import PreviewIcon from '@mui/icons-material/Preview';
import { BusinessContext } from '../context/BusinessContext'; // To potentially get currency symbols
import currencies from '../constants/currencies.json';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AdminCategoriesSettings from '../components/settings/AdminCategoriesSettings';
import AdminBulkAddBusinessesModal from '../components/dashboard/AdminBulkAddBusinessesModal';
import AdminBusinessSourcesModal from '../components/settings/AdminBusinessSourcesModal';
import { spacing } from '../utils/themeUtils';
import { isDemoUser } from '../utils/demoUtils';

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to format currency (symbol only, like Businesses.js)
const formatCurrency = (amount, currencyCode) => {
  if (amount === null || amount === undefined || !currencyCode) return 'N/A';
  const currencyData = currencies[currencyCode.toUpperCase()];
  const symbol = currencyData ? currencyData.symbol_native : '';
  return `${symbol}${Number(amount).toLocaleString()}`;
};

// Helper to calculate trial remaining days
const getTrialRemaining = (trialEndsAt) => {
  if (trialEndsAt === null || trialEndsAt === undefined) return "Not Set";

  const endDate = new Date(trialEndsAt);
  if (isNaN(endDate.getTime())) return "Invalid Date";

  const now = new Date();

  if (endDate <= now) return "Ended";

  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining`;
};

// Sorting helper functions
const getAdminStatusSortValue = (status, trialEndsAt) => {
  const trialRemaining = getTrialRemaining(trialEndsAt);
  switch (status) {
    case 'active': return 1;
    case 'trialing':
      if (trialRemaining === "Ended" || trialRemaining === "Invalid Date" || trialRemaining === "Not Set") return 3;
      return 2;
    case 'canceled': return 4;
    case 'past_due': return 5;
    case 'unpaid': return 6;
    case 'incomplete': return 7;
    default: return 8;
  }
};

function descendingComparator(a, b, orderBy) {
  let aValue = a[orderBy];
  let bValue = b[orderBy];

  if (orderBy === 'subscription_status') {
    aValue = getAdminStatusSortValue(a.subscription_status, a.trial_ends_at);
    bValue = getAdminStatusSortValue(b.subscription_status, b.trial_ends_at);
  } else if (orderBy === 'created_at' || orderBy === 'last_login_at' || orderBy === 'last_active_at') {
    const aDate = aValue ? new Date(aValue).getTime() : null;
    const bDate = bValue ? new Date(bValue).getTime() : null;

    if (aDate === null && bDate === null) return 0;
    if (aDate === null) return 1;
    if (bDate === null) return -1;

    if (bDate < aDate) return -1;
    if (bDate > aDate) return 1;
    return 0;
  } else if (orderBy === 'businesses_count') {
    aValue = a.businesses.length;
    bValue = b.businesses.length;
  } else if (typeof aValue === 'string' && typeof bValue === 'string') {
    aValue = aValue.toLowerCase();
    bValue = bValue.toLowerCase();
  }

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'email', numeric: false, label: 'User Email', minWidth: { xs: 180, md: 250 }, maxWidth: { xs: 220, md: 300 } },
  { id: 'created_at', numeric: false, label: 'Signup Date', minWidth: { xs: 90, md: 120 }, width: { xs: 'auto', md: 'auto' } },
  { id: 'last_login_at', numeric: false, label: 'Last Login', minWidth: { xs: 90, md: 120 }, width: { xs: 'auto', md: 'auto' } },
  { id: 'last_active_at', numeric: false, label: 'Last Active', minWidth: { xs: 90, md: 120 }, width: { xs: 'auto', md: 'auto' } },
  { id: 'subscription_status', numeric: false, label: 'Status', minWidth: { xs: 100, md: 120 }, width: { xs: 'auto', md: 'auto' } },
  { id: 'businesses_count', numeric: true, label: 'Businesses', disableSorting: false, minWidth: { xs: 80, md: 100 }, width: { xs: 'auto', md: 'auto' } },
  { id: 'expand', numeric: false, label: '', disableSorting: true, width: { xs: 40, md: 50 } }
];

// Update renderBusinessStatus to return elements matching Businesses.js exactly
const renderBusinessStatus = (status, isMonitored, theme) => {
  if (status === 'pending') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <TimelapseIcon sx={{
          fontSize: '1rem',
          mr: 0.5,
          color: '#F57C00',
        }} />
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#F57C00' }}>
          Processing
        </Typography>
      </Box>
    );
  } else if (isMonitored) {
    return (
      <Tooltip title="Monitored">
        <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.success.dark }}>
          <CheckCircleOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Monitored</Typography>
        </Box>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title="Not Monitored">
        <Typography variant="body2" sx={{ color: '#78909C', fontStyle: 'italic' }}>
          Not Monitored
        </Typography>
      </Tooltip>
    );
  }
};

// Helper function to format optional dates
const formatOptionalDate = (dateString) => {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return 'Invalid Date';
  }
};

const formatErrorMessage = (error) => {
  if (!error) return "";
  if (typeof error === 'string') return error;
  if (error.detail && typeof error.detail === 'string') return error.detail;
  if (error.detail && Array.isArray(error.detail)) return error.detail.map(e => e.msg || String(e)).join(', ');
  if (error.msg) return error.msg;
  if (Array.isArray(error)) return error.map(e => e.msg || String(e)).join(', ');
  try {
    const detailString = JSON.stringify(error.detail);
    if (detailString && detailString.length < 100) return detailString;
  } catch (e) { /* ignore stringify errors */ }
  return 'An unexpected error occurred.';
};

const AdminPage = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const { currencySymbol } = useContext(BusinessContext);
  const [filterText, setFilterText] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('email');
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [loadingReviewCounts, setLoadingReviewCounts] = useState(new Set());
  const [loadedReviewCounts, setLoadedReviewCounts] = useState(new Set());
  
  // New states for admin functionality
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [selectedCategoriesData, setSelectedCategoriesData] = useState(null);
  const [runningAnalysis, setRunningAnalysis] = useState(new Set());
  const [addBusinessModalOpen, setAddBusinessModalOpen] = useState(false);
  const [selectedUserForAdd, setSelectedUserForAdd] = useState(null);
  const [updatingMonitoring, setUpdatingMonitoring] = useState(new Set());
  
  // New states for analysis confirmation
  const [analysisConfirmOpen, setAnalysisConfirmOpen] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState(null);
  const [reviewLimit, setReviewLimit] = useState('');
  
  // New states for sources modal
  const [sourcesModalOpen, setSourcesModalOpen] = useState(false);
  const [selectedSourcesData, setSelectedSourcesData] = useState(null);
  
  // New states for reviews processing job functionality
  const [dailyJobStats, setDailyJobStats] = useState(null);
  const [dailyJobLoading, setDailyJobLoading] = useState(false);
  const [dailyJobConfirmOpen, setDailyJobConfirmOpen] = useState(false);
  const [dailyJobMessage, setDailyJobMessage] = useState('');

  useEffect(() => {
    const fetchAdminData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/admin/users`, { credentials: 'include' });
        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Access Denied: Administrator privileges required.');
          } else {
            const errorData = await response.json();
            throw new Error(errorData.detail || `Failed to fetch admin data (Status: ${response.status})`);
          }
        }
        const data = await response.json();
        setUsersData(data.users || []);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError(err.message || 'An error occurred while loading admin data.');
      } finally {
        setLoading(false);
      }
    };

    const fetchInitialStats = async () => {
      try {
        const response = await fetch(`${API_URL}/admin/daily-job-stats`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const stats = await response.json();
          setDailyJobStats(stats);
        }
      } catch (error) {
        console.error('Error fetching initial stats:', error);
        // Don't show error for this, it's optional
      }
    };

    fetchAdminData();
    fetchInitialStats();
  }, []);

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
  };

  const filteredUsersData = usersData.filter(user => {
    const searchTerm = filterText.toLowerCase();
    if (!searchTerm) return true;

    const emailMatch = user.email.toLowerCase().includes(searchTerm);
    const businessMatch = user.businesses.some(biz =>
      biz.name.toLowerCase().includes(searchTerm)
    );
    return emailMatch || businessMatch;
  });

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedAndFilteredUsersData = useMemo(() => {
    return stableSort(filteredUsersData, getComparator(order, orderBy));
  }, [filteredUsersData, order, orderBy]);

  const renderStatus = (status, trialEndsAt) => {
    const trialRemaining = getTrialRemaining(trialEndsAt);

    switch (status) {
      case 'trialing':
        let trialText = 'Trial';
        let trialColor = theme.palette.warning.dark;
        if (trialRemaining === "Not Set") {
          trialText = 'Trial (null)';
          trialColor = theme.palette.error.dark;
        } else if (trialRemaining === "Ended") {
          trialText = 'Trial (Ended)';
          trialColor = theme.palette.error.dark;
        } else if (trialRemaining === "Invalid Date") {
          trialText = 'Trial (Invalid Date)';
          trialColor = theme.palette.error.dark;
        } else {
          trialText = `Trial (${trialRemaining})`;
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: trialColor }}>
            <TimelapseIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {trialText}
          </Box>
        );
      case 'active':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.success.dark }}>
            <CheckCircleOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            Subscribed
          </Box>
        );
      case 'canceled':
      case 'past_due':
      case 'unpaid':
      case 'incomplete':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.error.dark }}>
            <HighlightOffIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Box>
        );
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.grey[600] }}>
            <HelpOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {status || 'Unknown'}
          </Box>
        );
    }
  };

  // Admin action handlers
  const handleEditCategories = (userId, businessId, businessName) => {
    setSelectedCategoriesData({ userId, businessId, businessName });
    setCategoriesModalOpen(true);
  };

  const handleRunAnalysisClick = (userId, businessId, businessName) => {
    setPendingAnalysis({ userId, businessId, businessName });
    setAnalysisConfirmOpen(true);
  };

  const handleConfirmAnalysis = async () => {
    if (!pendingAnalysis) return;
    
    const { userId, businessId, businessName } = pendingAnalysis;
    const key = `${userId}-${businessId}`;
    
    setAnalysisConfirmOpen(false);
    setPendingAnalysis(null);
    setRunningAnalysis(prev => new Set([...prev, key]));
    
    try {
      // Build URL with optional limit parameter
      let url = `${API_URL}/admin/users/${userId}/businesses/${businessId}/run-analysis`;
      if (reviewLimit && parseInt(reviewLimit) > 0) {
        url += `?limit=${parseInt(reviewLimit)}`;
      }
      
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setError(null);
      console.log('Analysis started successfully:', result);
      setReviewLimit(''); // Reset the limit input
    } catch (err) {
      console.error('Error running analysis:', err);
      setError(`Failed to run analysis for ${businessName}: ${err.message}`);
    } finally {
      setRunningAnalysis(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleCancelAnalysis = () => {
    setAnalysisConfirmOpen(false);
    setPendingAnalysis(null);
    setReviewLimit(''); // Reset the limit input
  };

  const handleAddBusinesses = (userId, userEmail) => {
    setSelectedUserForAdd({ userId, userEmail });
    setAddBusinessModalOpen(true);
  };

  const handleCategoriesSuccess = () => {
    setCategoriesModalOpen(false);
    setSelectedCategoriesData(null);
    // Optionally refresh data
  };

  const handleManageSources = (userId, businessId, businessName) => {
    setSelectedSourcesData({ userId, businessId, businessName });
    setSourcesModalOpen(true);
  };

  const handleSourcesSuccess = () => {
    setSourcesModalOpen(false);
    setSelectedSourcesData(null);
    // Optionally refresh data
  };

  const handleAddBusinessSuccess = async () => {
    setAddBusinessModalOpen(false);
    setSelectedUserForAdd(null);
    // Refresh admin data to show new businesses
    const response = await fetch(`${API_URL}/admin/users`, { credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      setUsersData(data.users || []);
    }
  };

  const handleToggleMonitoring = async (userId, businessId, businessName, currentStatus) => {
    const key = `${userId}-${businessId}`;
    setUpdatingMonitoring(prev => new Set([...prev, key]));
    
    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/businesses/${businessId}/monitoring-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_monitored: !currentStatus }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Update the local state to reflect the change
      setUsersData(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              businesses: user.businesses.map(biz => {
                if (biz.id === businessId) {
                  return { ...biz, is_monitored: !currentStatus };
                }
                return biz;
              })
            };
          }
          return user;
        })
      );

      setError(null);
      console.log(`Monitoring ${!currentStatus ? 'enabled' : 'disabled'} for ${businessName}`);
    } catch (err) {
      console.error('Error updating monitoring status:', err);
      setError(`Failed to update monitoring status for ${businessName}: ${err.message}`);
    } finally {
      setUpdatingMonitoring(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  // Reviews processing job functions
  const fetchDailyJobStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/daily-job-stats`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch daily job stats: ${response.status}`);
      }
      
      const stats = await response.json();
      setDailyJobStats(stats);
      return stats;
    } catch (error) {
      console.error('Error fetching daily job stats:', error);
      setError(`Failed to fetch daily job stats: ${error.message}`);
      return null;
    }
  };

  const handleDailyJobClick = async () => {
    setDailyJobLoading(true);
    const stats = await fetchDailyJobStats();
    setDailyJobLoading(false);
    
    if (stats) {
      setDailyJobConfirmOpen(true);
    }
  };

  const handleConfirmDailyJob = async () => {
    setDailyJobConfirmOpen(false);
    setDailyJobLoading(true);
    setDailyJobMessage('');
    
    try {
      const response = await fetch(`${API_URL}/admin/trigger-daily-job`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDailyJobMessage(`✅ ${data.message}. Check logs for detailed progress.`);
      } else {
        const errorData = await response.json();
        setDailyJobMessage(`❌ Failed: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error triggering daily job:', error);
      setDailyJobMessage('❌ Error triggering daily job');
    } finally {
      setDailyJobLoading(false);
    }
  };

  const handleCancelDailyJob = () => {
    setDailyJobConfirmOpen(false);
  };

  const fetchReviewCounts = async (userId) => {
    if (loadedReviewCounts.has(userId) || loadingReviewCounts.has(userId)) {
      return; // Already loaded or loading
    }

    setLoadingReviewCounts(prev => new Set([...prev, userId]));
    
    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/review-counts`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch review counts: ${response.status}`);
      }
      
      const reviewCountsByBusiness = await response.json();
      
      // Update users data with the fetched review counts
      setUsersData(prevUsers => 
        prevUsers.map(user => {
          if (user.id === userId) {
            return {
              ...user,
              businesses: user.businesses.map(business => ({
                ...business,
                review_counts_by_source: reviewCountsByBusiness[business.id] || {}
              }))
            };
          }
          return user;
        })
      );
      
      setLoadedReviewCounts(prev => new Set([...prev, userId]));
    } catch (err) {
      console.error('Error fetching review counts:', err);
      setError(`Failed to load review counts: ${err.message}`);
    } finally {
      setLoadingReviewCounts(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleCopyDemoLink = async (businessId, businessName) => {
    try {
      const baseUrl = window.location.origin;
      const demoUrl = `${baseUrl}/demo?businesses=${businessId}`;
      
      await navigator.clipboard.writeText(demoUrl);
      // Show success message temporarily
      setError(`✅ Demo link copied for ${businessName}!`);
      
      // Clear the message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy demo link:', err);
      setError(`Failed to copy demo link for ${businessName}`);
    }
  };

  return (
    <Box sx={{
      minHeight: '100%',
      background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
      display: 'flex',
      flexDirection: 'column',
      px: spacing.pageHorizontal,
      pt: spacing.pageTop,
      pb: spacing.pageBottom,
      opacity: 0,
      animation: 'fadeIn 1s forwards',
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      }
    }}>
      <Container maxWidth={false} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: { xs: 2, lg: 4 }, display: 'flex', alignItems: 'center' }}>
          <AdminPanelSettingsIcon sx={{ fontSize: { xs: '1.75rem', lg: '2.5rem' }, color: '#1976d2', mr: 1 }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '1.75rem', lg: '2.5rem' }, color: '#0A2540' }}>
            Admin Overview
          </Typography>
        </Box>

        {/* System Stats Overview */}
        {dailyJobStats && (
          <Box sx={{ mb: 3, p: 3, backgroundColor: '#f8fffe', borderRadius: '12px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0A2540', display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1, color: '#4caf50' }} />
              System Overview
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, 
              gap: 2 
            }}>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50', mb: 0.5 }}>
                  {dailyJobStats.active_users}
                </Typography>
                <Typography variant="body2" sx={{ color: '#78909C', fontSize: '0.85rem' }}>
                  Active Users
                </Typography>
                <Typography variant="caption" sx={{ color: '#bdbdbd' }}>
                  of {dailyJobStats.total_users} total
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3', mb: 0.5 }}>
                  {dailyJobStats.monitored_businesses}
                </Typography>
                <Typography variant="body2" sx={{ color: '#78909C', fontSize: '0.85rem' }}>
                  Monitored Businesses
                </Typography>
                <Typography variant="caption" sx={{ color: '#bdbdbd' }}>
                  of {dailyJobStats.total_businesses} total
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800', mb: 0.5 }}>
                  {dailyJobStats.chunks_to_process}
                </Typography>
                <Typography variant="body2" sx={{ color: '#78909C', fontSize: '0.85rem' }}>
                  Processing Chunks
                </Typography>
                <Typography variant="caption" sx={{ color: '#bdbdbd' }}>
                  {dailyJobStats.chunk_size} businesses each
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0', mb: 0.5 }}>
                  {dailyJobStats.businesses_with_subscriptions}
                </Typography>
                <Typography variant="body2" sx={{ color: '#78909C', fontSize: '0.85rem' }}>
                  With Subscriptions
                </Typography>
                <Typography variant="caption" sx={{ color: '#bdbdbd' }}>
                  Zembra monitoring
                </Typography>
              </Box>
            </Box>

          </Box>
        )}

        {/* Reviews Processing Job Section */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f8ff', borderRadius: '12px', border: '1px solid rgba(33, 150, 243, 0.1)' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#0A2540' }}>
            Reviews Processing Job
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Button
              variant="contained"
              onClick={handleDailyJobClick}
              disabled={dailyJobLoading}
              startIcon={<PlayArrowIcon />}
              sx={{
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: '180px'
              }}
            >
              {dailyJobLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Process Reviews'}
            </Button>
            {dailyJobMessage && (
              <Typography variant="body2" sx={{ 
                fontSize: '0.9rem',
                p: 1,
                backgroundColor: dailyJobMessage.startsWith('✅') ? '#e8f5e8' : '#ffebee',
                borderRadius: '6px',
                border: dailyJobMessage.startsWith('✅') ? '1px solid #4caf50' : '1px solid #f44336',
                color: dailyJobMessage.startsWith('✅') ? '#2e7d32' : '#c62828',
                flex: 1
              }}>
                {dailyJobMessage}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: '#78909C', fontSize: '0.85rem' }}>
            Manually trigger the reviews processing job that normally runs on a daily schedule. This will process all monitored businesses and fetch new reviews.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        <Box sx={{ mb: 2, mt: 1, px: { xs: 0, md: 0 }, maxWidth: '400px' }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Filter by email or business name..."
            value={filterText}
            onChange={handleFilterChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: filterText && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear filter"
                    onClick={() => setFilterText('')}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              }
            }}
          />
        </Box>

        <Card sx={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
          borderRadius: { xs: '12px', sm: '16px', md: '24px' },
          border: '1px solid rgba(25, 118, 210, 0.1)',
          boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
          }
        }}>
          <TableContainer sx={{ 
            margin: 0, 
            padding: { xs: 1, md: 2 },
            overflowX: 'auto', // Enable horizontal scrolling on mobile
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            }
          }}>
            <Table
              size="medium"
              aria-label="admin users table"
              sx={{
                tableLayout: 'auto',
                minWidth: { xs: 600, md: 800 }, // Ensure minimum table width for scrolling
                '& th, & td': {
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  borderBottom: '1px solid rgba(25, 118, 210, 0.08)',
                  lineHeight: 1.4,
                  verticalAlign: 'top'
                },
                '& th': {
                  fontWeight: 700,
                  color: '#0A2540',
                  borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                },
                '& tr:last-child td': {
                  borderBottom: 0,
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.numeric ? 'right' : 'left'}
                      sortDirection={orderBy === headCell.id ? order : false}
                      sx={{
                        width: headCell.width,
                        minWidth: headCell.minWidth,
                        maxWidth: headCell.maxWidth,
                        fontWeight: 700,
                        color: '#0A2540',
                        borderBottom: '2px solid rgba(25, 118, 210, 0.1)',
                        px: { xs: 1, sm: 2 },
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        lineHeight: 1.4,
                        verticalAlign: 'top'
                      }}
                    >
                      {headCell.disableSorting ? (
                        headCell.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : 'asc'}
                          onClick={() => handleSortRequest(headCell.id)}
                        >
                          {headCell.label}
                          {orderBy === headCell.id ? (
                            <Box component="span" sx={{ position: 'absolute', top: -10, right: -10, opacity: 0 }} />
                          ) : null}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} thickness={4} sx={{ color: '#1976d2' }} />
                    </TableCell>
                  </TableRow>
                ) : sortedAndFilteredUsersData.length === 0 && !error ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: '0.95rem', color: '#666666' }}>
                        {filterText ? 'No users match your filter.' : 'No user data found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAndFilteredUsersData.map((user) => (
                    <React.Fragment key={user.id}>
                      <TableRow
                        sx={{
                          '& > *': { borderBottom: 'unset' }
                        }}
                      >
                        <TableCell component="th" scope="row" sx={{ 
                          fontWeight: 500, 
                          color: '#0A2540', 
                          borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined,
                          maxWidth: { xs: 220, md: 300 },
                          minWidth: { xs: 180, md: 250 }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                wordBreak: 'break-word', // Allow breaking long emails
                                lineHeight: 1.3
                              }}
                            >
                              {user.email}
                            </Typography>
                            {user.role === 'admin' && (
                              <Tooltip title="Administrator">
                                <AdminPanelSettingsIcon sx={{ fontSize: '1.1rem', ml: 0.5, verticalAlign: 'middle', color: theme.palette.primary.main }} />
                              </Tooltip>
                            )}
                            {isDemoUser(user.email) && (
                              <Tooltip title="Demo Account">
                                <PreviewIcon sx={{ fontSize: '1.1rem', ml: 0.5, verticalAlign: 'middle', color: '#ff9800' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#546E7A', borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined }}>
                          {formatOptionalDate(user.created_at)}
                        </TableCell>
                        <TableCell sx={{ color: '#546E7A', borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined }}>
                          {formatOptionalDate(user.last_login_at)}
                        </TableCell>
                        <TableCell sx={{ color: '#546E7A', borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined }}>
                          {formatOptionalDate(user.last_active_at)}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500, borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined }}>
                          {renderStatus(user.subscription_status, user.trial_ends_at)}
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined }}>
                          {user.businesses.length.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ borderBottom: expandedRowIds.includes(user.id) ? 'none' : undefined }}>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => {
                              const currentExpanded = expandedRowIds.includes(user.id);
                              if (currentExpanded) {
                                setExpandedRowIds(expandedRowIds.filter(id => id !== user.id));
                              } else {
                                setExpandedRowIds([...expandedRowIds, user.id]);
                                fetchReviewCounts(user.id); // Fetch review counts when opening drawer
                              }
                            }}
                          >
                            {expandedRowIds.includes(user.id) ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={headCells.length}>
                          <Collapse in={expandedRowIds.includes(user.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1, p: 2, backgroundColor: 'rgba(25, 118, 210, 0.02)', borderRadius: '8px' }}>
                              {/* Add Business Button */}
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <Button
                                  size="small"
                                  startIcon={<PersonAddIcon />}
                                  onClick={() => handleAddBusinesses(user.id, user.email)}
                                  variant="outlined"
                                  sx={{ 
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  Add Business
                                </Button>
                              </Box>
                              
                              {user.businesses.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#78909C', fontStyle: 'italic', textAlign: 'center', width: '100%', py: 2 }}>
                                  No businesses added
                                </Typography>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                  {user.businesses.map((biz) => (
                                    <Paper key={biz.id} elevation={0} sx={{
                                      p: 1.5,
                                      borderRadius: '8px',
                                      border: `1px solid ${theme.palette.divider}`,
                                    }}>
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.75 }}>
                                            {biz.name}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: theme.palette.grey[600], display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                                            ID: {biz.id}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: theme.palette.grey[700], display: 'block', mb: 1 }}>
                                            {biz.address}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: theme.palette.grey[600], display: 'block', mb: 0.25 }}>
                                            Type: {biz.primary_type || 'N/A'}
                                          </Typography>
                                          
                                                                    {/* Review Counts by Source */}
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ color: theme.palette.grey[600], display: 'block', mb: 0.5 }}>
                              Reviews by Source:
                            </Typography>
                            {loadingReviewCounts.has(user.id) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" sx={{ color: theme.palette.grey[500], fontStyle: 'italic' }}>
                                  Loading review counts...
                                </Typography>
                              </Box>
                            ) : biz.review_counts_by_source && Object.keys(biz.review_counts_by_source).length > 0 ? (
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                {Object.entries(biz.review_counts_by_source).map(([source, count]) => (
                                                                                    <Chip
                                    key={source}
                                    label={`${source.charAt(0).toUpperCase() + source.slice(1)}: ${count.toLocaleString()}`}
                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      fontSize: '0.7rem',
                                                      height: '20px',
                                                      backgroundColor: source === 'google' ? '#4285f4' : 
                                                                       source === 'tripadvisor' ? '#00aa6c' :
                                                                       source === 'justeat' ? '#ff6900' :
                                                                       source === 'deliveroo' ? '#00ccbc' :
                                                                       source === 'ubereats' ? '#06c167' :
                                                                       source === 'booking' ? '#003580' :

                                                                       source === 'yelp' ? '#d32323' :
                                                                       source === 'designmynight' ? '#8e24aa' :
                                                                       source === 'trustpilot' ? '#00b67a' :
                                                                       theme.palette.grey[400],
                                                      color: 'white',
                                                      borderColor: 'transparent',
                                                      fontWeight: 500
                                                    }}
                                                  />
                                                ))}
                                                                                <Typography variant="caption" sx={{ 
                                  color: theme.palette.grey[700], 
                                  fontWeight: 600, 
                                  ml: 1,
                                  fontSize: '0.75rem'
                                }}>
                                  Total: {Object.values(biz.review_counts_by_source).reduce((sum, count) => sum + count, 0).toLocaleString()}
                                </Typography>
                                                                            </Box>
                            ) : loadedReviewCounts.has(user.id) ? (
                              <Typography variant="caption" sx={{ color: theme.palette.grey[500], fontStyle: 'italic' }}>
                                No reviews found
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: theme.palette.grey[500], fontStyle: 'italic' }}>
                                Open drawer to load review counts
                              </Typography>
                            )}
                                          </Box>
                                          
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#1976d2',
                                                  fontWeight: 600,
                                                  fontSize: 'inherit'
                                                }}>
                                                {formatCurrency(biz.turnover, biz.currency)}
                                                <Box component="span" sx={{
                                                  fontWeight: 'normal',
                                                  color: theme.palette.grey[600],
                                                  ml: 0.5
                                                }}>
                                                  ({biz.currency || 'N/A'})
                                                </Box>
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              {renderBusinessStatus(biz.status, biz.is_monitored, theme)}
                                            </Box>
                                          </Box>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                                          <Button
                                            size="small"
                                            startIcon={updatingMonitoring.has(`${user.id}-${biz.id}`) ? 
                                              <CircularProgress size={12} color="inherit" /> : 
                                              biz.is_monitored ? <VisibilityOffIcon /> : <VisibilityIcon />
                                            }
                                            onClick={() => handleToggleMonitoring(user.id, biz.id, biz.name, biz.is_monitored)}
                                            variant={biz.is_monitored ? "outlined" : "contained"}
                                            disabled={updatingMonitoring.has(`${user.id}-${biz.id}`)}
                                            sx={{ 
                                              fontSize: '0.75rem', 
                                              minWidth: 'auto',
                                              ...(biz.is_monitored ? {} : {
                                                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                                                '&:hover': {
                                                  background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                                                }
                                              })
                                            }}
                                          >
                                            {updatingMonitoring.has(`${user.id}-${biz.id}`) ? 
                                              'Updating...' : 
                                              biz.is_monitored ? 'Disable' : 'Enable'
                                            }
                                          </Button>
                                          <Button
                                            size="small"
                                            startIcon={<CategoryIcon />}
                                            onClick={() => handleEditCategories(user.id, biz.id, biz.name)}
                                            variant="outlined"
                                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                                          >
                                            Categories
                                          </Button>
                                          <Button
                                            size="small"
                                            startIcon={<LinkIcon />}
                                            onClick={() => handleManageSources(user.id, biz.id, biz.name)}
                                            variant="outlined"
                                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                                          >
                                            Sources
                                          </Button>
                                          <Button
                                            size="small"
                                            startIcon={runningAnalysis.has(`${user.id}-${biz.id}`) ? 
                                              <CircularProgress size={12} color="inherit" /> : 
                                              <PlayArrowIcon />
                                            }
                                            onClick={() => handleRunAnalysisClick(user.id, biz.id, biz.name)}
                                            variant="contained"
                                            disabled={runningAnalysis.has(`${user.id}-${biz.id}`)}
                                            sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                                          >
                                            {runningAnalysis.has(`${user.id}-${biz.id}`) ? 'Running...' : 'Run Analysis'}
                                          </Button>
                                          {/* Only show Demo Link button for demo user */}
                                          {isDemoUser(user.email) && (
                                            <Button
                                              size="small"
                                              startIcon={<LinkIcon />}
                                              onClick={() => handleCopyDemoLink(biz.id, biz.name)}
                                              variant="outlined"
                                              sx={{ 
                                                fontSize: '0.75rem', 
                                                minWidth: 'auto',
                                                background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                                color: 'white',
                                                '&:hover': {
                                                  background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                                                }
                                              }}
                                            >
                                              Demo Link
                                            </Button>
                                          )}
                                        </Box>
                                      </Box>
                                    </Paper>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

      </Container>

      {/* Analysis Confirmation Dialog */}
      <Dialog
        open={analysisConfirmOpen}
        onClose={handleCancelAnalysis}
        aria-labelledby="analysis-confirm-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="analysis-confirm-dialog-title">
          Run Analysis for {pendingAnalysis?.businessName}?
          {reviewLimit && parseInt(reviewLimit) > 0 && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Analyzing last {reviewLimit} reviews
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose how many reviews to analyze. Leave empty to analyze all reviews.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Recent Reviews (optional)"
            type="number"
            fullWidth
            variant="outlined"
            value={reviewLimit}
            onChange={(e) => setReviewLimit(e.target.value)}
            placeholder="e.g., 20"
            helperText="Enter a number to analyze only the most recent reviews, or leave empty to analyze all reviews"
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAnalysis}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmAnalysis} 
            variant="contained"
            startIcon={<PlayArrowIcon />}
          >
            {reviewLimit && parseInt(reviewLimit) > 0 
              ? `Run Analysis (${reviewLimit} reviews)` 
              : 'Run Analysis (All reviews)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reviews Processing Job Confirmation Dialog */}
      <Dialog
        open={dailyJobConfirmOpen}
        onClose={handleCancelDailyJob}
        aria-labelledby="reviews-job-confirm-dialog-title"
      >
        <DialogTitle id="reviews-job-confirm-dialog-title">
          Process Reviews Now?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will manually trigger the reviews processing job that normally runs automatically on a daily schedule.
          </DialogContentText>
          
          <DialogContentText sx={{ mb: 2 }}>
            <strong>What this process does:</strong>
          </DialogContentText>
          <DialogContentText component="div" sx={{ mb: 2, ml: 2 }}>
            • <strong>Fetches new reviews</strong> from Google and TripAdvisor for all monitored businesses<br/>
            • <strong>Collects reviews</strong> from active Zembra subscriptions (JustEat, Deliveroo, UberEats, etc.)<br/>
            • <strong>Automatically analyzes</strong> all newly fetched reviews using existing custom categories<br/>
            • <strong>Cleans up subscriptions</strong> for businesses that are no longer monitored
          </DialogContentText>
          
          <DialogContentText sx={{ fontSize: '0.9rem', fontStyle: 'italic', color: '#666' }}>
            Unlike "Run Analysis" which only analyzes existing reviews, this fetches fresh reviews first, then analyzes them.
          </DialogContentText>
          
          <DialogContentText sx={{ mt: 2, color: '#f57c00', fontWeight: 500 }}>
            ⚠️ This will trigger background processing jobs. Monitor the logs for progress.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDailyJob}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDailyJob} 
            variant="contained"
            startIcon={<PlayArrowIcon />}
            sx={{
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              }
            }}
          >
            Process Reviews
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Categories Modal */}
      {categoriesModalOpen && selectedCategoriesData && (
        <AdminCategoriesSettings
          userId={selectedCategoriesData.userId}
          businessId={selectedCategoriesData.businessId}
          businessName={selectedCategoriesData.businessName}
          open={categoriesModalOpen}
          onClose={() => {
            setCategoriesModalOpen(false);
            setSelectedCategoriesData(null);
          }}
          onSuccess={handleCategoriesSuccess}
        />
      )}

      {/* Admin Add Business Modal */}
      {addBusinessModalOpen && selectedUserForAdd && (
        <AdminBulkAddBusinessesModal
          open={addBusinessModalOpen}
          onClose={() => {
            setAddBusinessModalOpen(false);
            setSelectedUserForAdd(null);
          }}
          onBulkAddSuccess={handleAddBusinessSuccess}
          API_URL={API_URL}
          currencies={currencies}
          formatErrorMessage={formatErrorMessage}
          userId={selectedUserForAdd.userId}
          userEmail={selectedUserForAdd.userEmail}
        />
      )}

      {/* Admin Business Sources Modal */}
      {sourcesModalOpen && selectedSourcesData && (
        <AdminBusinessSourcesModal
          open={sourcesModalOpen}
          onClose={() => {
            setSourcesModalOpen(false);
            setSelectedSourcesData(null);
          }}
          businessId={selectedSourcesData.businessId}
          businessName={selectedSourcesData.businessName}
          onSuccess={handleSourcesSuccess}
          API_URL={API_URL}
        />
      )}
    </Box>
  );
};

export default AdminPage; 